import {Button, Dialog, DialogActions, DialogContent, Stack, Typography} from "@mui/material";
import {MyDialogTitle} from "@/components/dialog/MyDialogTitle.tsx";
import {z} from "zod";
import {zodResolver} from "@hookform/resolvers/zod";
import {FormProvider, useForm} from "react-hook-form";
import {FormTextField} from "@/components/form/FormTextField.tsx";
import {useCallback} from "react";
import {ManagementCompanyFragmentFragment, useCreateManagementCompanyMutation} from "@/graphql/graphql";
import toast from "react-hot-toast";

interface CreateNewManagementCompanyDialogProps {
  open: boolean;
  onClose: () => void;
  onCreateSuccess?: (value: ManagementCompanyFragmentFragment) => Promise<void>;
}

const schema = z.object({
  name: z.string(),
  address: z.string(),
  phoneNumber: z.string().regex(new RegExp(
    /^0\d{1,4}-\d{1,4}-\d{3,4}$/
  ), '半角ハイフンを含んだ半角数字で入力してください'),
  registrationNumber: z.string(),
  remarks: z.string(),
})

export function CreateNewManagementCompanyDialog(
  {open, onClose, onCreateSuccess}: CreateNewManagementCompanyDialogProps
) {
  
  const create = useCreateManagementCompanyMutation()
  
  const form = useForm({
    defaultValues: {
      name: '',
      address: '',
      phoneNumber: '',
      registrationNumber: '',
      remarks: '',
    },
    resolver: zodResolver(schema),
    mode: "onChange"
  })
  
  const onSubmit = useCallback(async (
    value: z.infer<typeof schema>
  ) => {
    const toastId = toast.loading('作成中...')
    await create.mutateAsync({
      input: value
    }).then(
      (res) => {
        if (res.createManagementCompany) {
          toast.success('作成しました', {id: toastId})
          onClose()
          if (onCreateSuccess) {
            onCreateSuccess(res.createManagementCompany)
          }
        }
      }
    ).catch(
      () => {
        toast.error('作成に失敗しました', {id: toastId})
      }
    )
  }, [create, onClose, onCreateSuccess])
  
  return <Dialog open={open} onClose={onClose} maxWidth={'sm'} fullWidth>
    <MyDialogTitle
      onClose={onClose}
    >
      新規作成
    </MyDialogTitle>
    <FormProvider {...form}>
      <form onSubmit={form.handleSubmit(onSubmit)}>
        <DialogContent>
          <Stack spacing={1}>
            
            <Typography variant={'formLabel'}>名前</Typography>
            <FormTextField name={'name'} control={form.control} placeholder={'株式会社Paradis'}/>
            <Typography variant={'formLabel'}>住所</Typography>
            <FormTextField name={'address'} control={form.control} placeholder={"東京都千代田区1-1-1"}/>
            <Typography variant={'formLabel'}>電話番号</Typography>
            <FormTextField name={'phoneNumber'} control={form.control} placeholder={'012-3456-7890'}/>
            <Typography variant={'formLabel'}>登録番号</Typography>
            <FormTextField name={'registrationNumber'} control={form.control} placeholder={'東京都知事(0)第00000号'}/>
            <Typography variant={'formLabel'}>備考</Typography>
            <FormTextField name={'remarks'} control={form.control} placeholder={'備考'}/>
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={onClose}>キャンセル</Button>
          <Button type={'submit'} variant={'contained'} disabled={!form.formState.isValid}>作成</Button>
        </DialogActions>
      </form>
    </FormProvider>
  
  </Dialog>
}
